import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Building2, Maximize2 } from 'lucide-react';

const DesignSection = ({ images }) => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });

  const imageScale = useTransform(scrollYProgress, [0, 0.5], [1.1, 1]);

  return (
    <section ref={containerRef} className="py-32 bg-gradient-to-b from-purple-50/30 to-white relative overflow-hidden">
      <div className="container mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-6">
            <Building2 className="w-4 h-4" />
            Reka Bentuk Projek
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Visualisasi Kompleks Darul Ummah
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Lihat reka bentuk kompleks pendidikan Islam bersepadu yang bakal dibina di atas tanah wakaf 5 ekar
          </p>
        </motion.div>

        {/* Main aerial poster */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.9, ease: [0.33, 1, 0.68, 1] }}
          className="max-w-5xl mx-auto mb-8"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
            <motion.img
              style={{ scale: imageScale }}
              src={images.posterAerial}
              alt="Pandangan udara Kompleks Darul Ummah"
              className="w-full h-auto"
            />
            <div className="absolute top-6 right-6 w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 className="w-5 h-5 text-white" />
            </div>
          </div>
        </motion.div>

        {/* Two building renders */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8 }}
            whileHover={{ y: -8 }}
            className="group"
          >
            <div className="rounded-3xl overflow-hidden shadow-xl border border-purple-100">
              <div className="overflow-hidden">
                <img
                  src={images.buildingFront}
                  alt="Pandangan hadapan bangunan KDU"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Pandangan Hadapan</h3>
                <p className="text-gray-600">Reka bentuk fasad bangunan utama dengan seni bina Islamik moden</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8 }}
            whileHover={{ y: -8 }}
            className="group"
          >
            <div className="rounded-3xl overflow-hidden shadow-xl border border-purple-100">
              <div className="overflow-hidden">
                <img
                  src={images.buildingSide}
                  alt="Pandangan sisi bangunan KDU"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Pandangan Sisi</h3>
                <p className="text-gray-600">Susun atur ruang pembelajaran yang selesa dan kondusif</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DesignSection;
